import { motion, useReducedMotion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { RefObject } from 'react'
import { memo, useEffect, useMemo, useState } from 'react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'

import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { MarketStatusBadge } from '@/components/widget/market-status-badge'
import { OverlayScrollY, OverlayThumbY } from '@/components/widget/overlay-scroll-y'
import { formatExpiryLabel } from '@/features/dashboard/components/watchlist-wizard/asset-grouping'
import {
  computeVisibleRowKeys,
  splitWizardPricePayloadTiers,
  useNoQuoteTimeouts,
  useWatchlistWizardPrices,
  type VisibleRange,
  WIZARD_PRICE_RANGE_BUFFER,
  type WizardQuote,
} from '@/features/dashboard/components/watchlist-wizard/use-watchlist-wizard-prices'
import { WatchlistGroupSelectButton } from '@/features/dashboard/components/watchlist-wizard/watchlist-group-select-button'
import { WatchlistInstrumentRow } from '@/features/dashboard/components/watchlist-wizard/watchlist-instrument-row'
import {
  type DetailRow,
  formatPrice,
  groupByExchange,
  isAssetGroup,
  representativeInstrumentForAsset,
  type WizardGroup,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-grouping'
import {
  type LiveWatchlistTicker,
  useWatchlistLiveTickers,
} from '@/features/dashboard/hooks/use-watchlist-live-ticker'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { cn, safeParseFloat } from '@/lib/utils'
import { displayPriceOf } from '@/lib/utils/display-price'
import { Instrument, InstrumentKind } from '@/types/generated/common/types'

// Start warming the first screen before Virtuoso finishes measuring. Waiting
// for its first rangeChanged callback leaves the pane unsubscribed while it is
// hidden or settling its layout, which can make a scroll become the event that
// finally triggers the first prices. Twenty flattened rows safely covers the
// tallest supported dialog; the normal 30-row network buffer is added later.
const INITIAL_VISIBLE_RANGE: VisibleRange = { startIndex: 0, endIndex: 19 }
const EMPTY_ENTRY_KEYS = new Set<string>()

// Entering the pane (see `entryKey`) staggers its first rows in, the same rhythm as the
// master list's own fade-in (watchlist-wizard.tsx's MASTER_ROW_STAGGER_*).
const DETAIL_ROW_STAGGER_DELAY_S = 0.03
const DETAIL_ROW_STAGGER_MAX_INDEX = 8
// Long enough for the first screen of rows to mount and start their stagger; rows
// mounted after it (scrolling, a hover-driven asset swap) just appear.
const DETAIL_ENTRY_WINDOW_MS = 400

interface KeyedVisibleRange {
  resetKey: string | null
  range: VisibleRange
}

interface WatchlistDetailPanelProps {
  activeGroup: WizardGroup | null
  assetOnlySelection: boolean
  rows: DetailRow[]
  virtuosoRef: RefObject<VirtuosoHandle | null>
  collapsedSections: Set<string>
  singleMode: boolean
  existingKeys: Set<string>
  selectedKeys: Set<string>
  highlightedKey: string | null
  searching: boolean
  query: string
  exchangeFallbackLabel: string
  emptyLabel: string
  marketCountLabel: (count: number) => string
  selectAllLabel: (label: string) => string
  deselectAllLabel: (label: string) => string
  /** Whether the dialog + this pane are actually in front of the user —
   * drives price subscriptions. Not the same as keyboard `activePane`,
   * which flips back to 'master' on every master-row hover even while the
   * venue pane stays visually on screen on desktop. */
  pricesEnabled: boolean
  /** Clears the local price cache when the selected asset changes. */
  priceResetKey: string | null
  /** Bumped each time the user steps into this pane (click / tap / Enter / →): its rows
   *  remount and stagger in. Not bumped by a hover preview, so sweeping the master list
   *  on desktop doesn't replay the animation on every row it passes. */
  entryKey: number
  onSelectInstrument: (instrument: Instrument) => void
  onActivateInstrument: (instrument: Instrument) => void
  onToggleGroup: (instruments: Instrument[]) => void
  onToggleSection: (key: string) => void
}

export function WatchlistDetailPanel({
  activeGroup,
  assetOnlySelection,
  rows,
  virtuosoRef,
  collapsedSections,
  singleMode,
  existingKeys,
  selectedKeys,
  highlightedKey,
  searching,
  query,
  exchangeFallbackLabel,
  emptyLabel,
  marketCountLabel,
  selectAllLabel,
  deselectAllLabel,
  pricesEnabled,
  priceResetKey,
  entryKey,
  onSelectInstrument,
  onActivateInstrument,
  onToggleGroup,
  onToggleSection,
}: WatchlistDetailPanelProps) {
  const [reportedRangeState, setReportedRangeState] = useState<KeyedVisibleRange>(() => ({
    resetKey: priceResetKey,
    range: INITIAL_VISIBLE_RANGE,
  }))
  const [debouncedRangeState, setDebouncedRangeState] = useState<KeyedVisibleRange>(() => ({
    resetKey: priceResetKey,
    range: INITIAL_VISIBLE_RANGE,
  }))

  // Range state belongs to the asset whose rows Virtuoso measured. On an
  // asset switch, use the eager first-screen range synchronously during
  // render instead of briefly applying the previous asset's scroll position
  // to a differently indexed row array.
  const reportedRange =
    reportedRangeState.resetKey === priceResetKey ? reportedRangeState.range : INITIAL_VISIBLE_RANGE
  const debouncedRange =
    debouncedRangeState.resetKey === priceResetKey
      ? debouncedRangeState.range
      : INITIAL_VISIBLE_RANGE

  // Genuine scroll-driven range changes still debounce, so a virtualized
  // list doesn't resubscribe on every intermediate scroll position.
  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedRangeState({ resetKey: priceResetKey, range: reportedRange }),
      150
    )
    return () => clearTimeout(timeout)
  }, [priceResetKey, reportedRange])

  const { visible, buffered } = useMemo(
    () => splitWizardPricePayloadTiers(rows, debouncedRange),
    [rows, debouncedRange]
  )

  // Two independent subscriptions/sessions on purpose — see
  // splitWizardPricePayloadTiers' comment. Both flush into the same
  // module-level wizardPriceStore, so the component only re-renders once
  // per actual price change regardless of which tier it came from.
  const priceMap = useWatchlistWizardPrices({
    tickerPayloads: visible.tickerPayloads,
    incrementalPayloads: visible.incrementalPayloads,
    enabled: pricesEnabled,
    resetKey: priceResetKey,
  })
  useWatchlistWizardPrices({
    tickerPayloads: buffered.tickerPayloads,
    incrementalPayloads: buffered.incrementalPayloads,
    enabled: pricesEnabled,
    resetKey: priceResetKey,
  })

  // Fallback for rows the wizard's own WS subscription above hasn't gotten a
  // tick for yet (see WatchlistDetailInstrumentRow) — one shared, range-
  // scoped subscription to the app's existing watchlist ticker map instead of
  // every row mounting its own. Same window as the WS "buffered" tier.
  const fallbackTickerKeys = useMemo(
    () => computeVisibleRowKeys(rows, debouncedRange, WIZARD_PRICE_RANGE_BUFFER),
    [rows, debouncedRange]
  )
  const fallbackTickerMap = useWatchlistLiveTickers(fallbackTickerKeys)
  // Rows still silent after the timeout have no market (e.g. untraded CME back-months): "—".
  const noQuoteKeys = useNoQuoteTimeouts(fallbackTickerKeys, {
    enabled: pricesEnabled,
    resetKey: priceResetKey,
  })

  // True for a short window after an entry: rows mounting in it stagger in.
  const reduceMotion = useReducedMotion()
  const [settledEntryKey, setSettledEntryKey] = useState(entryKey)
  const [virtuosoScroller, setVirtuosoScroller] = useState<HTMLElement | null>(null)
  const staggering = entryKey !== settledEntryKey && !reduceMotion
  useEffect(() => {
    if (entryKey === settledEntryKey) return
    const timeout = setTimeout(() => setSettledEntryKey(entryKey), DETAIL_ENTRY_WINDOW_MS)
    return () => clearTimeout(timeout)
  }, [entryKey, settledEntryKey])

  if (!activeGroup) {
    return (
      <div className="text-foreground/35 hidden h-full items-center justify-center px-6 text-center text-sm md:flex">
        {emptyLabel}
      </div>
    )
  }

  if (assetOnlySelection && isAssetGroup(activeGroup)) {
    return (
      <OverlayScrollY className="h-full" scrollerClassName="h-full">
        {groupByExchange(activeGroup.instruments).map((group) => {
          const representative = representativeInstrumentForAsset(group.instruments)
          const iconKey = getExchangeIconKey(group.exchange)
          return (
            <button
              key={group.exchange}
              type="button"
              disabled={!representative}
              onClick={() => representative && onSelectInstrument(representative)}
              className={cn(
                'border-foreground/7 hover:bg-foreground/4 active:bg-foreground/10 flex min-h-14 w-full items-center gap-3 border-b px-4 text-left disabled:opacity-40'
              )}
            >
              {iconKey ? <ExchangeIcon exchange={iconKey} className="size-7 rounded-full" /> : null}
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{group.label}</span>
              <MarketStatusBadge exchange={group.exchange} />
              <span className="text-foreground/35 text-xs">
                {marketCountLabel(group.instruments.length)}
              </span>
            </button>
          )
        })}
      </OverlayScrollY>
    )
  }

  const renderRow = (row: DetailRow) => {
    if (row.kind !== 'section') {
      const key = watchlistEntryKey(row.instrument.exchange, row.instrument.symbol)
      const alreadyAdded = existingKeys.has(key) && selectedKeys.has(key)
      const quote = priceMap[key]
      const fallbackTicker = fallbackTickerMap[key]
      return (
        <WatchlistDetailInstrumentRow
          row={row}
          quote={quote}
          fallbackTicker={fallbackTicker}
          noQuote={!quote && !fallbackTicker && noQuoteKeys.has(key)}
          alreadyAdded={alreadyAdded}
          checked={alreadyAdded || selectedKeys.has(key)}
          highlighted={highlightedKey === key}
          singleMode={singleMode}
          searching={searching}
          query={query}
          exchangeFallbackLabel={exchangeFallbackLabel}
          onActivate={onActivateInstrument}
        />
      )
    }

    const collapsed = collapsedSections.has(row.key)
    const depth = row.depth ?? 0
    return (
      <div
        className={cn(
          'bg-foreground/10 text-foreground/80 sticky top-1 z-10 my-1 flex items-center gap-2 px-2 py-1.5',
          depth === 0 && 'ml-0',
          depth === 1 && 'ml-2',
          depth === 2 && 'ml-4'
        )}
      >
        {!singleMode ? (
          <div className="[&>button]:border-0">
            <WatchlistGroupSelectButton
              instruments={row.instruments}
              label={row.label}
              existingKeys={EMPTY_ENTRY_KEYS}
              selectedKeys={selectedKeys}
              selectAllLabel={selectAllLabel(row.label)}
              deselectAllLabel={deselectAllLabel(row.label)}
              onToggle={onToggleGroup}
            />
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => onToggleSection(row.key)}
          aria-expanded={!collapsed}
          className={cn('flex min-w-0 flex-1 items-center gap-2 self-stretch text-left')}
        >
          <span className="truncate text-sm font-semibold tracking-tight uppercase">
            {row.label}
          </span>
          <span className="text-foreground/50 shrink-0 text-sm font-medium tabular-nums">
            {row.count}
          </span>
          <ChevronDown
            className={cn('ml-auto size-4 transition-transform', !collapsed && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      </div>
    )
  }

  return (
    <div className="group/scroll-y relative h-full">
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={(el) => setVirtuosoScroller(el instanceof HTMLElement ? el : null)}
        className="scrollbar-none h-full"
        data={rows}
        computeItemKey={(_, row) => row.key}
        increaseViewportBy={220}
        rangeChanged={(range) => setReportedRangeState({ resetKey: priceResetKey, range })}
        itemContent={(index, row) => (
          // Keyed by the entry, so stepping in remounts the visible rows and replays it.
          <motion.div
            key={entryKey}
            initial={staggering ? { opacity: 0, y: 6 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.2,
              ease: [0.16, 1, 0.3, 1],
              delay: Math.min(index, DETAIL_ROW_STAGGER_MAX_INDEX) * DETAIL_ROW_STAGGER_DELAY_S,
            }}
          >
            {renderRow(row)}
          </motion.div>
        )}
      />
      <OverlayThumbY scroller={virtuosoScroller} />
    </div>
  )
}

interface WatchlistDetailInstrumentRowProps {
  row: Extract<DetailRow, { kind: 'instrument' }>
  quote: WizardQuote | undefined
  /** Looked up by the parent from a single batched, visible-range-scoped
   *  subscription (see `useWatchlistLiveTickers`) rather than this row
   *  subscribing on its own. */
  fallbackTicker: LiveWatchlistTicker | undefined
  /** Subscribed past the no-quote timeout with nothing received: show "—", not loading. */
  noQuote: boolean
  alreadyAdded: boolean
  checked: boolean
  highlighted: boolean
  singleMode: boolean
  searching: boolean
  query: string
  exchangeFallbackLabel: string
  onActivate: (instrument: Instrument) => void
}

const WatchlistDetailInstrumentRow = memo(function WatchlistDetailInstrumentRow({
  row,
  quote,
  fallbackTicker,
  noQuote,
  alreadyAdded,
  checked,
  highlighted,
  singleMode,
  searching,
  query,
  exchangeFallbackLabel,
  onActivate,
}: WatchlistDetailInstrumentRowProps) {
  const t = useTranslations('watchlistWizard')
  // Options read in implied vol, not price: a premium means little across strikes and
  // venues quoting in different units. A collapsed chain row's contract is the chain's
  // ATM (see showAtmIv); a drilled-in leaf shows its own mark IV.
  if (row.instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
    const iv = Number(quote?.iv)
    const { instrument } = row
    return (
      <WatchlistInstrumentRow
        instrument={instrument}
        indentation={row.indentation ?? 'detail'}
        alreadyAdded={alreadyAdded}
        checked={checked}
        highlighted={highlighted}
        singleMode={singleMode}
        searching={searching}
        query={query}
        exchangeFallbackLabel={exchangeFallbackLabel}
        // The option's own ticker is the only IV source (the fallback ticker map has none).
        price={quote ? (iv > 0 ? `${iv.toFixed(1)}%` : '—') : noQuote ? '—' : undefined}
        priceLoading={quote === undefined && !noQuote}
        priceCaption={row.showAtmIv ? t('atmIv') : t('markIv')}
        priceTitle={
          row.showAtmIv
            ? t('atmIvTitle', {
                expiry: formatExpiryLabel(Number(instrument.expirationTsMs ?? 0)),
                strike: formatPrice(instrument.strikePrice) ?? '—',
              })
            : quote?.price
              ? t('markPriceTitle', { price: formatPrice(quote.price) ?? quote.price })
              : undefined
        }
        titleOverride={row.label}
        subtitleOverride={row.sublabel}
        onActivate={onActivate}
      />
    )
  }
  const rawPrice = quote?.price || displayPriceOf(fallbackTicker)
  const changePercent =
    quote?.changePercent ??
    (fallbackTicker?.priceChangePercent24h
      ? safeParseFloat(fallbackTicker.priceChangePercent24h)
      : undefined)
  const formattedPrice = formatPrice(rawPrice)

  return (
    <WatchlistInstrumentRow
      instrument={row.instrument}
      indentation={row.indentation ?? 'detail'}
      alreadyAdded={alreadyAdded}
      checked={checked}
      highlighted={highlighted}
      singleMode={singleMode}
      searching={searching}
      query={query}
      exchangeFallbackLabel={exchangeFallbackLabel}
      // A tick without an own price, or none at all by the timeout, ends the skeleton: "—".
      price={formattedPrice ?? (quote || fallbackTicker || noQuote ? '—' : undefined)}
      priceLoading={formattedPrice === undefined && !quote && !fallbackTicker && !noQuote}
      changePercent={changePercent}
      titleOverride={row.label}
      subtitleOverride={row.sublabel}
      onActivate={onActivate}
    />
  )
})
